import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { CheckCircle2, Loader2, XCircle, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";

const POLL_INTERVAL_MS = 2000;
const MAX_ATTEMPTS = 12;

export default function PaymentSuccess() {
  const location = useLocation();
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const [status, setStatus] = useState("polling"); // polling | paid | failed | expired | timeout
  const [plan, setPlan] = useState(null);
  const attempts = useRef(0);
  const timer = useRef(null);

  const sessionId = new URLSearchParams(location.search).get("session_id");

  useEffect(() => {
    if (!sessionId) { setStatus("failed"); return; }
    const poll = async () => {
      attempts.current += 1;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setStatus("paid");
          setPlan(data.plan);
          refresh?.();
          return;
        }
        if (data.status === "expired" || data.payment_status === "expired") { setStatus("expired"); return; }
        if (data.status === "failed" || data.payment_status === "failed") { setStatus("failed"); return; }
        if (attempts.current >= MAX_ATTEMPTS) { setStatus("timeout"); return; }
        timer.current = setTimeout(poll, POLL_INTERVAL_MS);
      } catch {
        if (attempts.current >= MAX_ATTEMPTS) { setStatus("timeout"); return; }
        timer.current = setTimeout(poll, POLL_INTERVAL_MS);
      }
    };
    poll();
    return () => { if (timer.current) clearTimeout(timer.current); };
  }, [sessionId, refresh]);

  return (
    <div className="min-h-screen bg-background paper-grain grid place-items-center px-6">
      <div className="max-w-md w-full rounded-3xl border border-border bg-card p-10 text-center" data-testid="payment-success-card">
        {status === "polling" && (
          <>
            <Loader2 className="mx-auto mb-4 animate-spin text-primary" />
            <h1 className="text-3xl mb-2">Confirming your payment…</h1>
            <p className="text-muted-foreground">Hang tight, this only takes a moment.</p>
          </>
        )}
        {status === "paid" && (
          <>
            <CheckCircle2 className="mx-auto mb-4 text-primary" size={44} />
            <h1 className="text-3xl mb-2">You're in.</h1>
            <p className="text-muted-foreground mb-8">
              Welcome to Readlet {plan === "studio" ? "Studio" : "Deeper"}. All the good stuff is unlocked.
            </p>
            <Button data-testid="go-dashboard-btn" onClick={() => navigate("/dashboard")} className="rounded-full h-12 px-7">
              Open dashboard <ArrowRight size={18} className="ml-2" />
            </Button>
          </>
        )}
        {(status === "failed" || status === "expired") && (
          <>
            <XCircle className="mx-auto mb-4 text-destructive" size={44} />
            <h1 className="text-3xl mb-2">Payment didn't complete.</h1>
            <p className="text-muted-foreground mb-8">No charge was made. Feel free to try again.</p>
            <Button onClick={() => navigate("/")} className="rounded-full h-12 px-7">Back to home</Button>
          </>
        )}
        {status === "timeout" && (
          <>
            <Loader2 className="mx-auto mb-4 animate-spin text-primary" />
            <h1 className="text-3xl mb-2">Still processing…</h1>
            <p className="text-muted-foreground mb-8">
              Stripe may need another moment. You can go to your dashboard — your plan will update automatically.
            </p>
            <Button onClick={() => navigate("/dashboard")} className="rounded-full h-12 px-7">Open dashboard</Button>
          </>
        )}
      </div>
    </div>
  );
}
